import { useFormik } from 'formik';
import { AGING_DATE_BASIS, PaymentMethod } from 'services/api/legalCorporationConfig/types';
import { HttpError } from 'services/api/types';
import { useIntlByKey } from 'services/i18n/hooks/useIntlByKey';
import { logger } from 'services/logs/logger';
import * as yup from 'yup';

import { SettingsFormKeys, SettingsInformation } from './settingsFormValues';

export const useSettingsFormik = (
  initialFormValues: SettingsInformation,
  onSubmit: (settingsInfo: SettingsInformation) => Promise<void>,
  onSubmitError: (error: HttpError) => void,
) => {
  const settingsFormIntl = useIntlByKey('settings-form');

  const requiredFieldMessage = settingsFormIntl('form.required-field');
  const invalidFieldMessage = settingsFormIntl('form.invalid-field');

  const paymentLimitSchema = (method: PaymentMethod) =>
    yup
      .number()
      .typeError(invalidFieldMessage)
      .when([SettingsFormKeys.IS_PAYMENT_ENABLED, SettingsFormKeys.ALLOWED_PAYMENT_METHODS], {
        is: (isPaymentEnabled: boolean, methods: PaymentMethod[]) =>
          isPaymentEnabled && Boolean(methods?.includes(method)),
        then: (schema) =>
          schema
            .required(requiredFieldMessage)
            .moreThan(0, settingsFormIntl('form.payment-limit.invalid')),
        otherwise: (schema) => schema.notRequired(),
      });

  const validationSchema = yup.object({
    [SettingsFormKeys.CONTACT_EMAIL]: yup
      .string()
      .email(invalidFieldMessage)
      .required(requiredFieldMessage),

    [SettingsFormKeys.SUBSCRIPTION_REQUEST_EMAIL]: yup
      .string()
      .email(invalidFieldMessage)
      .required(requiredFieldMessage),

    // Payment fields are only displayed, hence validated, when online payment is enabled
    [SettingsFormKeys.SURCHARGE_PERCENTAGE]: yup
      .number()
      .typeError(invalidFieldMessage)
      .when(SettingsFormKeys.IS_PAYMENT_ENABLED, {
        is: true,
        then: (schema) =>
          schema
            .required(requiredFieldMessage)
            .min(0, settingsFormIntl('form.surcharge-percentage.invalid'))
            .max(100, settingsFormIntl('form.surcharge-percentage.invalid')),
        otherwise: (schema) => schema.notRequired(),
      }),

    [SettingsFormKeys.MAX_ALLOWED_DAYS_TO_PAY]: yup
      .number()
      .typeError(invalidFieldMessage)
      .when([SettingsFormKeys.IS_PAYMENT_ENABLED, SettingsFormKeys.IS_PAYMENT_CUTOFF_ENABLED], {
        is: (isPaymentEnabled: boolean, isPaymentCutoffEnabled: boolean) =>
          isPaymentEnabled && isPaymentCutoffEnabled,
        then: (schema) =>
          schema
            .required(requiredFieldMessage)
            .integer(invalidFieldMessage)
            .min(0, invalidFieldMessage),
        otherwise: (schema) => schema.notRequired(),
      }),

    [SettingsFormKeys.RECONCILIATION_EMAILS]: yup
      .array()
      .of(yup.string().email(invalidFieldMessage).required())
      .test(
        'max-length',
        settingsFormIntl('form.reconciliation-emails.too-long'),
        (emails) => (emails ?? []).join(',').length <= 1000,
      ),

    [SettingsFormKeys.ALLOWED_PAYMENT_METHODS]: yup.array().of(yup.string()),
    [SettingsFormKeys.ACH_LIMIT]: paymentLimitSchema('ACH'),
    [SettingsFormKeys.CREDIT_CARD_LIMIT]: paymentLimitSchema('CreditCard'),

    [SettingsFormKeys.AGING_BUCKETS]: yup
      .array()
      .of(yup.number().integer().moreThan(0).required())
      .min(1, requiredFieldMessage),

    [SettingsFormKeys.AGING_DATE_BASIS]: yup
      .string()
      .oneOf([...AGING_DATE_BASIS], invalidFieldMessage)
      .required(requiredFieldMessage),

    [SettingsFormKeys.INVITATION_REPLY_TO_EMAIL]: yup.string().email(invalidFieldMessage),
  });

  return useFormik({
    initialValues: initialFormValues,
    validationSchema,
    onSubmit: async (values: SettingsInformation) => {
      try {
        await onSubmit(values);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        logger.logError('Error submitting formik form', error);
        onSubmitError(error.response as HttpError);
      }
    },
  });
};

export type SettingsFormik = ReturnType<typeof useSettingsFormik>;
